import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import type { PaginatedListDto } from '../models/common/paginated-list.dto';
import type {
  AddInterviewInterviewerDto,
  ApplicationStageHistoryDto,
  CandidateApplicationActionDto,
  CandidateApplicationDto,
  CandidateCommunicationDto,
  CandidateRequisitionMatchDto,
  CandidateProfileSectionsDto,
  CandidateResumeDto,
  CandidateDto,
  AssignRecruitmentWorkflowDto,
  CreateCandidateApplicationDto,
  CreateCandidateDto,
  CreateHiringRequestDto,
  CreateInterviewScheduleDto,
  CreateJobRequisitionDto,
  CreateOfferLetterDto,
  CreateRecruitmentEmailAccountDto,
  CreateRecruitmentEmailTemplateDto,
  CreateRecruitmentPipelineStatusDto,
  CreateRecruitmentQuestionTemplateDto,
  CreateRecruitmentStageDto,
  CreateRecruitmentWorkflowDto,
  GenerateRecruitmentWorkflowDraftDto,
  HiringRequestDto,
  ImportCandidateFromRecruitmentEmailDto,
  InterviewActionDto,
  InterviewEvaluationDto,
  InterviewInterviewerDto,
  InterviewScheduleDto,
  JobRequisitionDto,
  LinkRecruitmentEmailToApplicationDto,
  LinkRecruitmentEmailToCandidateDto,
  MoveCandidateApplicationStageDto,
  OfferLetterDto,
  RecordOfferResponseDto,
  RecruitmentApplicationPipelineDto,
  RecruitmentEmailAccountDto,
  RecruitmentEmailAccountProvider,
  RecruitmentEmailImapFolderDto,
  RecruitmentEmailOAuthConnectUrlDto,
  RecruitmentEmailOAuthProviderDto,
  RecruitmentEmailMessageDto,
  RecruitmentEmailMessageQueryDto,
  RecruitmentEmailSyncResultDto,
  RecruitmentEmailTemplateDto,
  RecruitmentEmailUnreadCountDto,
  RecruitmentJobCandidatePipelineDto,
  RecruitmentPipelineStatusDto,
  RecruitmentQuestionTemplateDto,
  RecruitmentQueryDto,
  RecruitmentStageDto,
  RecruitmentTriageCreateAndApplyDto,
  RecruitmentTriageCreateCandidateDto,
  RecruitmentTriageDismissDto,
  RecruitmentWorkflowDraftDto,
  RecruitmentWorkflowDto,
  RescheduleInterviewDto,
  RespondToInterviewDto,
  SendOfferLetterDto,
  SendRecruitmentEmailDto,
  SubmitInterviewEvaluationDto,
  UpdateOfferLetterDto,
  UpdateRecruitmentApplicationStepEvaluationDto,
  UpdateHiringRequestDto,
  UpdateJobRequisitionDto,
  UpdateRecruitmentEmailAccountDto,
  UpdateRecruitmentEmailTemplateDto,
  UpdateRecruitmentPipelineStatusDto,
  UpdateRecruitmentQuestionTemplateDto,
  UpdateRecruitmentStageDto,
  UpdateRecruitmentWorkflowDto,
} from '../models/recruitment/recruitment.dto';

@Injectable({ providedIn: 'root' })
export class RecruitmentService {
  private readonly http = inject(HttpClient);

  private apiUrl(path: string): string {
    const base = environment.apiBaseUrl.replace(/\/+$/, '');
    return `${base}${path.startsWith('/') ? path : `/${path}`}`;
  }

  private params(query: RecruitmentQueryDto): HttpParams {
    let params = new HttpParams().set('skip', String(query.skip)).set('rowCount', String(query.rowCount));
    if (query.search?.trim()) params = params.set('search', query.search.trim());
    if (query.hiringRequestId?.trim()) params = params.set('hiringRequestId', query.hiringRequestId.trim());
    if (query.jobRequisitionId?.trim()) params = params.set('jobRequisitionId', query.jobRequisitionId.trim());
    if (query.candidateId?.trim()) params = params.set('candidateId', query.candidateId.trim());
    if (query.stageId?.trim()) params = params.set('stageId', query.stageId.trim());
    if (query.departmentId?.trim()) params = params.set('departmentId', query.departmentId.trim());
    if (query.branchId?.trim()) params = params.set('branchId', query.branchId.trim());
    if (query.status) params = params.set('status', String(query.status));
    return params;
  }

  hiringRequests(query: RecruitmentQueryDto): Observable<PaginatedListDto<HiringRequestDto>> {
    return this.http.get<PaginatedListDto<HiringRequestDto>>(this.apiUrl('/api/HiringRequests'), { params: this.params(query) });
  }

  createHiringRequest(model: CreateHiringRequestDto): Observable<HiringRequestDto> {
    return this.http.post<HiringRequestDto>(this.apiUrl('/api/HiringRequests'), model);
  }

  updateHiringRequest(model: UpdateHiringRequestDto): Observable<HiringRequestDto> {
    return this.http.put<HiringRequestDto>(this.apiUrl('/api/HiringRequests'), model);
  }

  submitHiringRequest(id: string): Observable<HiringRequestDto> {
    return this.http.post<HiringRequestDto>(this.apiUrl(`/api/HiringRequests/${encodeURIComponent(id)}/submit-to-workflow`), {});
  }

  approveHiringRequest(id: string): Observable<HiringRequestDto> {
    return this.http.post<HiringRequestDto>(this.apiUrl(`/api/HiringRequests/${encodeURIComponent(id)}/approve`), {});
  }

  cancelHiringRequest(id: string): Observable<HiringRequestDto> {
    return this.http.post<HiringRequestDto>(this.apiUrl(`/api/HiringRequests/${encodeURIComponent(id)}/cancel`), {});
  }

  convertHiringRequest(id: string): Observable<JobRequisitionDto> {
    return this.http.post<JobRequisitionDto>(this.apiUrl(`/api/HiringRequests/${encodeURIComponent(id)}/convert-to-requisition`), {});
  }

  jobRequisitions(query: RecruitmentQueryDto): Observable<PaginatedListDto<JobRequisitionDto>> {
    return this.http.get<PaginatedListDto<JobRequisitionDto>>(this.apiUrl('/api/JobRequisitions'), { params: this.params(query) });
  }

  createJobRequisition(model: CreateJobRequisitionDto): Observable<JobRequisitionDto> {
    return this.http.post<JobRequisitionDto>(this.apiUrl('/api/JobRequisitions'), model);
  }

  updateJobRequisition(model: UpdateJobRequisitionDto): Observable<JobRequisitionDto> {
    return this.http.put<JobRequisitionDto>(this.apiUrl('/api/JobRequisitions'), model);
  }

  openJobRequisition(id: string): Observable<JobRequisitionDto> {
    return this.http.post<JobRequisitionDto>(this.apiUrl(`/api/JobRequisitions/${encodeURIComponent(id)}/open`), {});
  }

  pauseJobRequisition(id: string): Observable<JobRequisitionDto> {
    return this.http.post<JobRequisitionDto>(this.apiUrl(`/api/JobRequisitions/${encodeURIComponent(id)}/pause`), {});
  }

  closeJobRequisition(id: string): Observable<JobRequisitionDto> {
    return this.http.post<JobRequisitionDto>(this.apiUrl(`/api/JobRequisitions/${encodeURIComponent(id)}/close`), {});
  }

  publishCareerPortal(id: string): Observable<JobRequisitionDto> {
    return this.http.post<JobRequisitionDto>(this.apiUrl(`/api/JobRequisitions/${encodeURIComponent(id)}/publish-career-portal`), {});
  }

  cancelJobRequisition(id: string): Observable<JobRequisitionDto> {
    return this.http.post<JobRequisitionDto>(this.apiUrl(`/api/JobRequisitions/${encodeURIComponent(id)}/cancel`), {});
  }

  candidates(query: RecruitmentQueryDto): Observable<PaginatedListDto<CandidateDto>> {
    return this.http.get<PaginatedListDto<CandidateDto>>(this.apiUrl('/api/Candidates'), { params: this.params(query) });
  }

  candidate(id: string): Observable<CandidateDto> {
    return this.http.get<CandidateDto>(this.apiUrl(`/api/Candidates/${encodeURIComponent(id)}`));
  }

  createCandidate(model: CreateCandidateDto): Observable<CandidateDto> {
    return this.http.post<CandidateDto>(this.apiUrl('/api/Candidates'), model);
  }

  deleteCandidate(id: string): Observable<void> {
    return this.http.delete<void>(this.apiUrl(`/api/Candidates/${encodeURIComponent(id)}`));
  }

  applications(query: RecruitmentQueryDto): Observable<PaginatedListDto<CandidateApplicationDto>> {
    return this.http.get<PaginatedListDto<CandidateApplicationDto>>(this.apiUrl('/api/CandidateApplications'), { params: this.params(query) });
  }

  createApplication(model: CreateCandidateApplicationDto): Observable<CandidateApplicationDto> {
    return this.http.post<CandidateApplicationDto>(this.apiUrl('/api/CandidateApplications'), model);
  }

  moveApplicationStage(id: string, model: MoveCandidateApplicationStageDto): Observable<CandidateApplicationDto> {
    return this.http.post<CandidateApplicationDto>(this.apiUrl(`/api/CandidateApplications/${encodeURIComponent(id)}/move-stage`), model);
  }

  rejectApplication(id: string, model: CandidateApplicationActionDto): Observable<CandidateApplicationDto> {
    return this.http.post<CandidateApplicationDto>(this.apiUrl(`/api/CandidateApplications/${encodeURIComponent(id)}/reject`), model);
  }

  hireApplication(id: string, model: CandidateApplicationActionDto): Observable<CandidateApplicationDto> {
    return this.http.post<CandidateApplicationDto>(this.apiUrl(`/api/CandidateApplications/${encodeURIComponent(id)}/hire`), model);
  }

  applicationStageHistory(id: string): Observable<ApplicationStageHistoryDto[]> {
    return this.http.get<ApplicationStageHistoryDto[]>(this.apiUrl(`/api/CandidateApplications/${encodeURIComponent(id)}/stage-history`));
  }

  applicationTimeline(id: string): Observable<CandidateCommunicationDto[]> {
    return this.http.get<CandidateCommunicationDto[]>(this.apiUrl(`/api/CandidateApplications/${encodeURIComponent(id)}/timeline`));
  }

  interviews(query: RecruitmentQueryDto): Observable<PaginatedListDto<InterviewScheduleDto>> {
    return this.http.get<PaginatedListDto<InterviewScheduleDto>>(this.apiUrl('/api/InterviewSchedules'), { params: this.params(query) });
  }

  scheduleInterview(model: CreateInterviewScheduleDto): Observable<InterviewScheduleDto> {
    return this.http.post<InterviewScheduleDto>(this.apiUrl('/api/InterviewSchedules'), model);
  }

  rescheduleInterview(id: string, model: RescheduleInterviewDto): Observable<InterviewScheduleDto> {
    return this.http.put<InterviewScheduleDto>(this.apiUrl(`/api/InterviewSchedules/${encodeURIComponent(id)}/reschedule`), model);
  }

  cancelInterview(id: string, model: InterviewActionDto): Observable<InterviewScheduleDto> {
    return this.http.post<InterviewScheduleDto>(this.apiUrl(`/api/InterviewSchedules/${encodeURIComponent(id)}/cancel`), model);
  }

  completeInterview(id: string, model: InterviewActionDto): Observable<InterviewScheduleDto> {
    return this.http.post<InterviewScheduleDto>(this.apiUrl(`/api/InterviewSchedules/${encodeURIComponent(id)}/complete`), model);
  }

  markInterviewNoShow(id: string, model: InterviewActionDto): Observable<InterviewScheduleDto> {
    return this.http.post<InterviewScheduleDto>(this.apiUrl(`/api/InterviewSchedules/${encodeURIComponent(id)}/no-show`), model);
  }

  interviewInterviewers(id: string): Observable<InterviewInterviewerDto[]> {
    return this.http.get<InterviewInterviewerDto[]>(this.apiUrl(`/api/InterviewSchedules/${encodeURIComponent(id)}/interviewers`));
  }

  addInterviewInterviewer(id: string, model: AddInterviewInterviewerDto): Observable<InterviewInterviewerDto> {
    return this.http.post<InterviewInterviewerDto>(this.apiUrl(`/api/InterviewSchedules/${encodeURIComponent(id)}/interviewers`), model);
  }

  removeInterviewInterviewer(id: string, userId: string): Observable<boolean> {
    return this.http.delete<boolean>(
      this.apiUrl(`/api/InterviewSchedules/${encodeURIComponent(id)}/interviewers/${encodeURIComponent(userId)}`),
    );
  }

  respondToInterview(id: string, model: RespondToInterviewDto): Observable<InterviewInterviewerDto> {
    return this.http.post<InterviewInterviewerDto>(this.apiUrl(`/api/InterviewSchedules/${encodeURIComponent(id)}/respond`), model);
  }

  interviewEvaluations(id: string): Observable<InterviewEvaluationDto[]> {
    return this.http.get<InterviewEvaluationDto[]>(this.apiUrl(`/api/InterviewSchedules/${encodeURIComponent(id)}/evaluations`));
  }

  submitInterviewEvaluation(id: string, model: SubmitInterviewEvaluationDto): Observable<InterviewEvaluationDto> {
    return this.http.put<InterviewEvaluationDto>(this.apiUrl(`/api/InterviewSchedules/${encodeURIComponent(id)}/evaluation`), model);
  }

  offers(query: RecruitmentQueryDto): Observable<PaginatedListDto<OfferLetterDto>> {
    return this.http.get<PaginatedListDto<OfferLetterDto>>(this.apiUrl('/api/OfferLetters'), { params: this.params(query) });
  }

  createOffer(model: CreateOfferLetterDto): Observable<OfferLetterDto> {
    return this.http.post<OfferLetterDto>(this.apiUrl('/api/OfferLetters'), model);
  }

  updateOffer(model: UpdateOfferLetterDto): Observable<OfferLetterDto> {
    return this.http.put<OfferLetterDto>(this.apiUrl('/api/OfferLetters'), model);
  }

  sendOffer(id: string, model: SendOfferLetterDto): Observable<OfferLetterDto> {
    return this.http.post<OfferLetterDto>(this.apiUrl(`/api/OfferLetters/${encodeURIComponent(id)}/send`), model);
  }

  cancelOffer(id: string): Observable<OfferLetterDto> {
    return this.http.post<OfferLetterDto>(this.apiUrl(`/api/OfferLetters/${encodeURIComponent(id)}/cancel`), {});
  }

  expireOffer(id: string): Observable<OfferLetterDto> {
    return this.http.post<OfferLetterDto>(this.apiUrl(`/api/OfferLetters/${encodeURIComponent(id)}/expire`), {});
  }

  recordOfferResponse(id: string, model: RecordOfferResponseDto): Observable<OfferLetterDto> {
    return this.http.post<OfferLetterDto>(this.apiUrl(`/api/OfferLetters/${encodeURIComponent(id)}/record-response`), model);
  }

  /**
   * Stage list. With `tenantWideOnly` the server returns only tenant-wide stages and ignores `jobRequisitionId`;
   * with a `jobRequisitionId` it returns that requisition's stages plus the tenant-wide ones; with neither it returns
   * every active stage of the tenant, requisition-scoped rows included.
   */
  /**
   * `includeInactive` is for the stage settings screen only: every other caller wants the active stages, and a
   * deactivated stage must not reach a picker. The server ignores it when `tenantWideOnly` is set.
   */
  stages(
    jobRequisitionId?: string | null,
    tenantWideOnly = false,
    includeInactive = false,
  ): Observable<RecruitmentStageDto[]> {
    let params = new HttpParams();
    if (tenantWideOnly) params = params.set('tenantWideOnly', 'true');
    else if (jobRequisitionId?.trim()) params = params.set('jobRequisitionId', jobRequisitionId.trim());
    if (includeInactive) params = params.set('includeInactive', 'true');
    return this.http.get<RecruitmentStageDto[]>(this.apiUrl('/api/RecruitmentStages'), { params });
  }

  createStage(model: CreateRecruitmentStageDto): Observable<RecruitmentStageDto> {
    return this.http.post<RecruitmentStageDto>(this.apiUrl('/api/RecruitmentStages'), model);
  }

  updateStage(model: UpdateRecruitmentStageDto): Observable<RecruitmentStageDto> {
    return this.http.put<RecruitmentStageDto>(this.apiUrl('/api/RecruitmentStages'), model);
  }

  deleteStage(id: string): Observable<boolean> {
    return this.http.delete<boolean>(this.apiUrl(`/api/RecruitmentStages/${encodeURIComponent(id)}`));
  }

  ensureDefaultStages(): Observable<RecruitmentStageDto[]> {
    return this.http.post<RecruitmentStageDto[]>(this.apiUrl('/api/RecruitmentStages/ensure-defaults'), {});
  }

  pipelineStatuses(): Observable<RecruitmentPipelineStatusDto[]> {
    return this.http.get<RecruitmentPipelineStatusDto[]>(this.apiUrl('/api/RecruitmentPipelineStatuses'));
  }

  createPipelineStatus(model: CreateRecruitmentPipelineStatusDto): Observable<RecruitmentPipelineStatusDto> {
    return this.http.post<RecruitmentPipelineStatusDto>(this.apiUrl('/api/RecruitmentPipelineStatuses'), model);
  }

  updatePipelineStatus(model: UpdateRecruitmentPipelineStatusDto): Observable<RecruitmentPipelineStatusDto> {
    return this.http.put<RecruitmentPipelineStatusDto>(
      this.apiUrl(`/api/RecruitmentPipelineStatuses/${encodeURIComponent(model.id)}`),
      model,
    );
  }

  deletePipelineStatus(id: string): Observable<boolean> {
    return this.http.delete<boolean>(this.apiUrl(`/api/RecruitmentPipelineStatuses/${encodeURIComponent(id)}`));
  }

  workflows(): Observable<RecruitmentWorkflowDto[]> {
    return this.http.get<RecruitmentWorkflowDto[]>(this.apiUrl('/api/RecruitmentWorkflows'));
  }

  workflow(id: string): Observable<RecruitmentWorkflowDto> {
    return this.http.get<RecruitmentWorkflowDto>(this.apiUrl(`/api/RecruitmentWorkflows/${encodeURIComponent(id)}`));
  }

  createWorkflow(model: CreateRecruitmentWorkflowDto): Observable<RecruitmentWorkflowDto> {
    return this.http.post<RecruitmentWorkflowDto>(this.apiUrl('/api/RecruitmentWorkflows'), model);
  }

  updateWorkflow(model: UpdateRecruitmentWorkflowDto): Observable<RecruitmentWorkflowDto> {
    return this.http.put<RecruitmentWorkflowDto>(
      this.apiUrl(`/api/RecruitmentWorkflows/${encodeURIComponent(model.id)}`),
      model,
    );
  }

  deleteWorkflow(id: string): Observable<boolean> {
    return this.http.delete<boolean>(this.apiUrl(`/api/RecruitmentWorkflows/${encodeURIComponent(id)}`));
  }

  generateWorkflowDraft(model: GenerateRecruitmentWorkflowDraftDto): Observable<RecruitmentWorkflowDraftDto> {
    return this.http.post<RecruitmentWorkflowDraftDto>(this.apiUrl('/api/RecruitmentWorkflows/ai-draft'), model);
  }

  assignWorkflowToJob(jobRequisitionId: string, model: AssignRecruitmentWorkflowDto): Observable<RecruitmentWorkflowDto> {
    return this.http.post<RecruitmentWorkflowDto>(
      this.apiUrl(`/api/JobRequisitions/${encodeURIComponent(jobRequisitionId)}/pipeline/workflow`),
      model,
    );
  }

  applicationPipeline(candidateApplicationId: string): Observable<RecruitmentApplicationPipelineDto> {
    return this.http.get<RecruitmentApplicationPipelineDto>(
      this.apiUrl(`/api/RecruitmentApplicationPipelines/${encodeURIComponent(candidateApplicationId)}`),
    );
  }

  updateApplicationStepEvaluation(
    applicationWorkflowStepId: string,
    model: UpdateRecruitmentApplicationStepEvaluationDto,
  ): Observable<RecruitmentApplicationPipelineDto> {
    return this.http.put<RecruitmentApplicationPipelineDto>(
      this.apiUrl(`/api/RecruitmentApplicationPipelines/steps/${encodeURIComponent(applicationWorkflowStepId)}/evaluation`),
      model,
    );
  }

  jobPipelineCandidates(jobRequisitionId: string): Observable<RecruitmentJobCandidatePipelineDto[]> {
    return this.http.get<RecruitmentJobCandidatePipelineDto[]>(
      this.apiUrl(`/api/JobRequisitions/${encodeURIComponent(jobRequisitionId)}/pipeline/candidates`),
    );
  }

  // --- Recruitment outbound email (SP2 / task 8) ---

  emailAccounts(): Observable<RecruitmentEmailAccountDto[]> {
    return this.http.get<RecruitmentEmailAccountDto[]>(this.apiUrl('/api/RecruitmentEmailAccounts'));
  }

  createEmailAccount(model: CreateRecruitmentEmailAccountDto): Observable<RecruitmentEmailAccountDto> {
    return this.http.post<RecruitmentEmailAccountDto>(this.apiUrl('/api/RecruitmentEmailAccounts'), model);
  }

  updateEmailAccount(model: UpdateRecruitmentEmailAccountDto): Observable<RecruitmentEmailAccountDto> {
    return this.http.put<RecruitmentEmailAccountDto>(this.apiUrl('/api/RecruitmentEmailAccounts'), model);
  }

  deleteEmailAccount(id: string): Observable<boolean> {
    return this.http.delete<boolean>(this.apiUrl(`/api/RecruitmentEmailAccounts/${encodeURIComponent(id)}`));
  }

  setDefaultEmailAccount(id: string): Observable<boolean> {
    return this.http.post<boolean>(this.apiUrl(`/api/RecruitmentEmailAccounts/${encodeURIComponent(id)}/set-default`), {});
  }

  testEmailAccount(id: string): Observable<boolean> {
    return this.http.post<boolean>(this.apiUrl(`/api/RecruitmentEmailAccounts/${encodeURIComponent(id)}/test-connection`), {});
  }

  listImapFolders(accountId: string): Observable<RecruitmentEmailImapFolderDto[]> {
    return this.http.get<RecruitmentEmailImapFolderDto[]>(
      this.apiUrl(`/api/RecruitmentEmailAccounts/${encodeURIComponent(accountId)}/imap-folders`),
    );
  }

  // --- Mailbox OAuth connection (SP3.2) ---

  oauthProviders(): Observable<RecruitmentEmailOAuthProviderDto[]> {
    return this.http.get<RecruitmentEmailOAuthProviderDto[]>(this.apiUrl('/api/RecruitmentEmailAccounts/oauth/providers'));
  }

  oauthConnectUrl(accountId: string, provider: RecruitmentEmailAccountProvider): Observable<RecruitmentEmailOAuthConnectUrlDto> {
    const params = new HttpParams().set('provider', String(provider));
    return this.http.get<RecruitmentEmailOAuthConnectUrlDto>(
      this.apiUrl(`/api/RecruitmentEmailAccounts/${encodeURIComponent(accountId)}/oauth/connect-url`),
      { params },
    );
  }

  refreshMailboxToken(id: string): Observable<RecruitmentEmailAccountDto> {
    return this.http.post<RecruitmentEmailAccountDto>(
      this.apiUrl(`/api/RecruitmentEmailAccounts/${encodeURIComponent(id)}/refresh-token`),
      {},
    );
  }

  disconnectMailbox(id: string): Observable<boolean> {
    return this.http.post<boolean>(this.apiUrl(`/api/RecruitmentEmailAccounts/${encodeURIComponent(id)}/disconnect`), {});
  }

  emailTemplates(): Observable<RecruitmentEmailTemplateDto[]> {
    return this.http.get<RecruitmentEmailTemplateDto[]>(this.apiUrl('/api/RecruitmentEmailTemplates'));
  }

  createEmailTemplate(model: CreateRecruitmentEmailTemplateDto): Observable<RecruitmentEmailTemplateDto> {
    return this.http.post<RecruitmentEmailTemplateDto>(this.apiUrl('/api/RecruitmentEmailTemplates'), model);
  }

  updateEmailTemplate(model: UpdateRecruitmentEmailTemplateDto): Observable<RecruitmentEmailTemplateDto> {
    return this.http.put<RecruitmentEmailTemplateDto>(this.apiUrl('/api/RecruitmentEmailTemplates'), model);
  }

  deleteEmailTemplate(id: string): Observable<boolean> {
    return this.http.delete<boolean>(this.apiUrl(`/api/RecruitmentEmailTemplates/${encodeURIComponent(id)}`));
  }

  activateEmailTemplate(id: string): Observable<RecruitmentEmailTemplateDto> {
    return this.http.post<RecruitmentEmailTemplateDto>(this.apiUrl(`/api/RecruitmentEmailTemplates/${encodeURIComponent(id)}/activate`), {});
  }

  deactivateEmailTemplate(id: string): Observable<RecruitmentEmailTemplateDto> {
    return this.http.post<RecruitmentEmailTemplateDto>(
      this.apiUrl(`/api/RecruitmentEmailTemplates/${encodeURIComponent(id)}/deactivate`),
      {},
    );
  }

  ensureDefaultEmailTemplates(): Observable<boolean> {
    return this.http.post<boolean>(this.apiUrl('/api/RecruitmentEmailTemplates/ensure-defaults'), {});
  }

  // --- SP5f application question templates ---

  /**
   * The tenant's question templates. Readable with the recruitment read permission and with
   * `Requisitions.Manage` as well as the settings permissions, so the requisition editor's picker is not silently
   * empty for someone who may edit jobs but not templates.
   */
  questionTemplates(): Observable<RecruitmentQuestionTemplateDto[]> {
    return this.http.get<RecruitmentQuestionTemplateDto[]>(this.apiUrl('/api/RecruitmentQuestionTemplates'));
  }

  questionTemplate(id: string): Observable<RecruitmentQuestionTemplateDto> {
    return this.http.get<RecruitmentQuestionTemplateDto>(
      this.apiUrl(`/api/RecruitmentQuestionTemplates/${encodeURIComponent(id)}`),
    );
  }

  createQuestionTemplate(model: CreateRecruitmentQuestionTemplateDto): Observable<RecruitmentQuestionTemplateDto> {
    return this.http.post<RecruitmentQuestionTemplateDto>(this.apiUrl('/api/RecruitmentQuestionTemplates'), model);
  }

  updateQuestionTemplate(model: UpdateRecruitmentQuestionTemplateDto): Observable<RecruitmentQuestionTemplateDto> {
    return this.http.put<RecruitmentQuestionTemplateDto>(this.apiUrl('/api/RecruitmentQuestionTemplates'), model);
  }

  /** Deleting a template in use clears it off this tenant's requisitions rather than failing. */
  deleteQuestionTemplate(id: string): Observable<boolean> {
    return this.http.delete<boolean>(this.apiUrl(`/api/RecruitmentQuestionTemplates/${encodeURIComponent(id)}`));
  }

  private emailMessageParams(query: RecruitmentEmailMessageQueryDto): HttpParams {
    let params = new HttpParams().set('skip', String(query.skip)).set('rowCount', String(query.rowCount));
    if (query.recruitmentEmailAccountId?.trim()) params = params.set('recruitmentEmailAccountId', query.recruitmentEmailAccountId.trim());
    if (query.candidateId?.trim()) params = params.set('candidateId', query.candidateId.trim());
    if (query.candidateApplicationId?.trim()) params = params.set('candidateApplicationId', query.candidateApplicationId.trim());
    if (query.jobRequisitionId?.trim()) params = params.set('jobRequisitionId', query.jobRequisitionId.trim());
    if (query.hasAttachments != null) params = params.set('hasAttachments', String(query.hasAttachments));
    if (query.linkedOnly != null) params = params.set('linkedOnly', String(query.linkedOnly));
    if (query.unreadOnly != null) params = params.set('unreadOnly', String(query.unreadOnly));
    if (query.classificationStatus != null) params = params.set('classificationStatus', String(query.classificationStatus));
    if (query.intent != null) params = params.set('intent', String(query.intent));
    if (query.processingStatus != null) params = params.set('processingStatus', String(query.processingStatus));
    if (query.hasResume != null) params = params.set('hasResume', String(query.hasResume));
    if (query.triageStatus != null) params = params.set('triageStatus', String(query.triageStatus));
    if (query.folderPath?.trim()) params = params.set('folderPath', query.folderPath.trim());
    if (query.includeImportedSent != null) params = params.set('includeImportedSent', String(query.includeImportedSent));
    if (query.search?.trim()) params = params.set('search', query.search.trim());
    return params;
  }

  sentEmails(query: RecruitmentEmailMessageQueryDto): Observable<PaginatedListDto<RecruitmentEmailMessageDto>> {
    return this.http.get<PaginatedListDto<RecruitmentEmailMessageDto>>(this.apiUrl('/api/RecruitmentEmails/sent'), {
      params: this.emailMessageParams(query),
    });
  }

  inbox(query: RecruitmentEmailMessageQueryDto): Observable<PaginatedListDto<RecruitmentEmailMessageDto>> {
    return this.http.get<PaginatedListDto<RecruitmentEmailMessageDto>>(this.apiUrl('/api/RecruitmentEmails/inbox'), {
      params: this.emailMessageParams(query),
    });
  }

  inboxUnreadCount(): Observable<RecruitmentEmailUnreadCountDto> {
    return this.http.get<RecruitmentEmailUnreadCountDto>(this.apiUrl('/api/RecruitmentEmails/inbox/unread-count'));
  }

  emailMessage(id: string): Observable<RecruitmentEmailMessageDto> {
    return this.http.get<RecruitmentEmailMessageDto>(this.apiUrl(`/api/RecruitmentEmails/message/${encodeURIComponent(id)}`));
  }

  /** Streams the stored attachment back as a blob so the caller can hand it to the browser as a file download. */
  downloadEmailAttachment(attachmentId: string): Observable<Blob> {
    return this.http.get(this.apiUrl(`/api/RecruitmentEmails/attachments/${encodeURIComponent(attachmentId)}/download`), {
      responseType: 'blob',
    });
  }

  reprocessEmail(id: string): Observable<RecruitmentEmailMessageDto> {
    return this.http.post<RecruitmentEmailMessageDto>(this.apiUrl(`/api/RecruitmentEmails/${encodeURIComponent(id)}/reprocess`), {});
  }

  syncMailboxNow(accountId: string): Observable<RecruitmentEmailSyncResultDto> {
    return this.http.post<RecruitmentEmailSyncResultDto>(
      this.apiUrl(`/api/RecruitmentEmails/${encodeURIComponent(accountId)}/sync-now`),
      {},
    );
  }

  markEmailRead(id: string): Observable<boolean> {
    return this.http.post<boolean>(this.apiUrl(`/api/RecruitmentEmails/${encodeURIComponent(id)}/mark-read`), {});
  }

  markEmailUnread(id: string): Observable<boolean> {
    return this.http.post<boolean>(this.apiUrl(`/api/RecruitmentEmails/${encodeURIComponent(id)}/mark-unread`), {});
  }

  ignoreEmail(id: string): Observable<boolean> {
    return this.http.post<boolean>(this.apiUrl(`/api/RecruitmentEmails/${encodeURIComponent(id)}/ignore`), {});
  }

  classifyEmail(id: string): Observable<RecruitmentEmailMessageDto> {
    return this.http.post<RecruitmentEmailMessageDto>(this.apiUrl(`/api/RecruitmentEmails/${encodeURIComponent(id)}/classify`), {});
  }

  parseEmailAttachments(id: string): Observable<RecruitmentEmailMessageDto> {
    return this.http.post<RecruitmentEmailMessageDto>(
      this.apiUrl(`/api/RecruitmentEmails/${encodeURIComponent(id)}/parse-attachments`),
      {},
    );
  }

  linkEmailToCandidate(model: LinkRecruitmentEmailToCandidateDto): Observable<RecruitmentEmailMessageDto> {
    return this.http.post<RecruitmentEmailMessageDto>(this.apiUrl('/api/RecruitmentEmails/link-to-candidate'), model);
  }

  linkEmailToApplication(model: LinkRecruitmentEmailToApplicationDto): Observable<RecruitmentEmailMessageDto> {
    return this.http.post<RecruitmentEmailMessageDto>(this.apiUrl('/api/RecruitmentEmails/link-to-application'), model);
  }

  createCandidateFromEmail(model: ImportCandidateFromRecruitmentEmailDto): Observable<CandidateDto> {
    return this.http.post<CandidateDto>(this.apiUrl('/api/RecruitmentEmails/create-candidate-from-email'), model);
  }

  // --- SP5a inbound triage (the three decisions with their own endpoints; "link to an existing candidate" is
  // `linkEmailToCandidate` above, which the server stamps `LinkedToExisting` in the same transaction) ---

  /** Answers with the new candidate's id. */
  triageCreateCandidate(model: RecruitmentTriageCreateCandidateDto): Observable<string> {
    return this.http.post<string>(this.apiUrl('/api/RecruitmentEmails/triage/create-candidate'), model);
  }

  /** Answers with the new candidate's id; the application is created through the ordinary create path. */
  triageCreateCandidateAndApply(model: RecruitmentTriageCreateAndApplyDto): Observable<string> {
    return this.http.post<string>(this.apiUrl('/api/RecruitmentEmails/triage/create-candidate-and-apply'), model);
  }

  triageDismiss(model: RecruitmentTriageDismissDto): Observable<boolean> {
    return this.http.post<boolean>(this.apiUrl('/api/RecruitmentEmails/triage/dismiss'), model);
  }

  /** The CV on the candidate's profile, or null when there is none (WN-1078). */
  candidateResume(candidateId: string): Observable<CandidateResumeDto | null> {
    return this.http.get<CandidateResumeDto | null>(this.apiUrl(`/api/Candidates/${encodeURIComponent(candidateId)}/resume`));
  }

  /** Gender and the structured skills, education, certifications and languages the candidate gave (WN-1079, WN-1080). */
  candidateProfileSections(candidateId: string): Observable<CandidateProfileSectionsDto> {
    return this.http.get<CandidateProfileSectionsDto>(this.apiUrl(`/api/Candidates/${encodeURIComponent(candidateId)}/profile`));
  }

  downloadCandidateResume(candidateId: string): Observable<Blob> {
    return this.http.get(this.apiUrl(`/api/Candidates/${encodeURIComponent(candidateId)}/resume/download`), {
      responseType: 'blob',
    });
  }

  candidateMatches(candidateId: string): Observable<CandidateRequisitionMatchDto[]> {
    return this.http.get<CandidateRequisitionMatchDto[]>(
      this.apiUrl(`/api/Candidates/${encodeURIComponent(candidateId)}/matches`),
    );
  }

  sendEmail(model: SendRecruitmentEmailDto): Observable<RecruitmentEmailMessageDto> {
    return this.http.post<RecruitmentEmailMessageDto>(this.apiUrl('/api/RecruitmentEmails/send'), model);
  }

  uploadEmailAttachment(file: File): Observable<string> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<string>(this.apiUrl('/api/RecruitmentEmails/attachments'), formData);
  }
}
